"use client";

import {
  type JobSharedPlatform,
  type JobSharedSource,
  trackJobShared,
} from "@workspace/analytics/events/job-shared";
import { Button } from "@workspace/ui/components/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@workspace/ui/components/popover";
import { useCallback } from "react";
import { toast } from "sonner";
import { Icons } from "@/components/icons";
import { getJobShareText, getJobShareUrl, getPlatformUrl } from "@/lib/share";

type ShareJobPopoverProps = {
  slug: string;
  title: string;
  orgName: string;
  jobId: string;
  organizationId: string;
  source: JobSharedSource;
  userType: "employee" | "employer" | "anonymous";
  align?: "start" | "end";
};

const PLATFORMS = [
  { id: "whatsapp", label: "WhatsApp", icon: Icons.message },
  { id: "facebook", label: "Facebook", icon: Icons.facebook },
  { id: "twitter", label: "X", icon: Icons.twitter },
  { id: "linkedin", label: "LinkedIn", icon: Icons.linkedin },
] as const;

export function ShareJobPopover({
  slug,
  title,
  orgName,
  jobId,
  organizationId,
  source,
  userType,
  align = "end",
}: ShareJobPopoverProps) {
  const url = getJobShareUrl(slug, title, orgName);
  const text = getJobShareText(title, orgName);

  const track = useCallback(
    (platform: JobSharedPlatform) => {
      trackJobShared({
        job_id: jobId,
        organization_id: organizationId,
        platform,
        source,
        user_type: userType,
      });
    },
    [jobId, organizationId, source, userType]
  );

  const handlePlatform = useCallback(
    (platform: string) => {
      track(platform as JobSharedPlatform);
      const shareUrl = getPlatformUrl(platform, url, text);
      window.open(shareUrl, "_blank", "noopener,noreferrer");
    },
    [url, text, track]
  );

  const handleCopy = useCallback(async () => {
    track("copy_link");
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", {
        description: "Job link copied to clipboard",
      });
    } catch {
      toast.error("Failed to copy", {
        description: "Please copy the URL manually",
      });
    }
  }, [url, track]);

  const handleNativeShare = useCallback(async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      track("native_share");
      try {
        await navigator.share({ title, text, url });
      } catch {
        // user cancelled
      }
    } else {
      handleCopy();
    }
  }, [title, text, url, handleCopy, track]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button className="h-8 gap-1" size="icon" variant="outline">
          <Icons.share className="h-3.5 w-3.5 text-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-3">
        <div className="grid grid-cols-4 gap-2">
          {PLATFORMS.map((platform) => (
            <Button
              className="flex size-14 flex-col items-center gap-1 p-1"
              key={platform.id}
              onClick={() => handlePlatform(platform.id)}
              title={platform.label}
              variant="ghost"
            >
              <platform.icon className="size-5" />
              <span className="text-[10px] text-muted-foreground leading-none">
                {platform.label}
              </span>
            </Button>
          ))}
          <Button
            className="flex size-14 flex-col items-center gap-1 p-1"
            onClick={handleCopy}
            title="Copy link"
            variant="ghost"
          >
            <Icons.copy className="size-5" />
            <span className="text-[10px] text-muted-foreground leading-none">
              Copy
            </span>
          </Button>
          <Button
            className="flex size-14 flex-col items-center gap-1 p-1"
            onClick={handleNativeShare}
            title="More options"
            variant="ghost"
          >
            <Icons.externalLink className="size-5" />
            <span className="text-[10px] text-muted-foreground leading-none">
              More
            </span>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
